import {
  ADMIN_PROVIDER_PAGE_SIZES,
  AdminProviderListQuery,
  type AdminProviderListQuery as AdminProviderListQueryType,
  type AdminProviderRow,
  type AdminProviderSort,
  formatDate,
  formatPhone,
  PROVIDER_KIND_LABELS,
  PROVIDER_KINDS,
  PROVIDER_STATUS_LABELS,
  PROVIDER_STATUSES,
  providerDisplayName,
} from '@myhome/contracts';
import { Button } from '@myhome/ui/components/button';
import { Input } from '@myhome/ui/components/input';
import { NativeSelect, NativeSelectOption } from '@myhome/ui/components/native-select';
import { CaretDown, CaretLeft, CaretRight, CaretUp, MagnifyingGlass } from '@myhome/ui/icons';
import { EmptyState } from '@myhome/ui/patterns/empty-state';
import { ListSkeleton } from '@myhome/ui/patterns/list-skeleton';
import { StatusBanner } from '@myhome/ui/patterns/status-banner';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, stripSearchParams, useNavigate } from '@tanstack/react-router';
import { createColumnHelper, tableFeatures, useTable } from '@tanstack/react-table';
import { useEffect, useId, useState } from 'react';
import { ProviderStatusBadge } from '../components/provider-status-badge';
import { adminProvidersQuery } from '../lib/queries';

/** What an empty URL means. Kept out of the address bar so links stay short. */
const DEFAULTS = AdminProviderListQuery.parse({});

export const Route = createFileRoute('/_shell/providers/')({
  validateSearch: AdminProviderListQuery,
  search: { middlewares: [stripSearchParams(DEFAULTS)] },
  component: ProvidersPage,
});

const features = tableFeatures({});
const helper = createColumnHelper<typeof features, AdminProviderRow>();
const emptyRows: AdminProviderRow[] = [];

const columns = helper.columns([
  helper.display({
    id: 'provider',
    header: () => <SortHeader sort="name" label="Provider" />,
    cell: ({ row }) => (
      <div className="flex min-w-0 flex-col">
        <Link
          to="/providers/$providerId"
          params={{ providerId: row.original.id }}
          className="truncate font-medium text-navy-900 underline-offset-4 hover:underline"
        >
          {providerDisplayName(row.original)}
        </Link>
        <span className="truncate text-eyebrow text-neutral-600">
          {row.original.contactFirstName} {row.original.contactLastName} · {row.original.email}
        </span>
      </div>
    ),
  }),
  helper.accessor('kind', {
    header: 'Kind',
    cell: ({ getValue }) => <span className="whitespace-nowrap">{PROVIDER_KIND_LABELS[getValue()]}</span>,
  }),
  helper.accessor('phone', {
    header: 'Phone',
    cell: ({ getValue }) => <span className="whitespace-nowrap tabular-nums">{formatPhone(getValue())}</span>,
  }),
  helper.display({
    id: 'coverage',
    header: 'Based in and categories',
    cell: ({ row }) => <CoverageCell row={row.original} />,
  }),
  helper.accessor('createdAt', {
    header: () => <SortHeader sort="createdAt" label="Signed up" />,
    cell: ({ getValue }) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatDate(new Date(getValue()), { year: 'numeric', month: 'short' })}
      </span>
    ),
  }),
  helper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => <ProviderStatusBadge status={getValue()} />,
  }),
]);

/** Which columns hide on a phone. */
const COLUMN_CLASS: Record<string, string> = {
  kind: 'hidden md:table-cell',
  phone: 'hidden lg:table-cell',
  coverage: 'hidden md:table-cell',
  createdAt: 'hidden lg:table-cell',
};

/**
 * The provider directory. Search, filters, sort and page all live in the URL,
 * so any view can be shared by link (apps/admin/CLAUDE.md). The review queue
 * and status changes arrive with the review feature.
 */
function ProvidersPage() {
  const search = Route.useSearch();
  // Same key as ProvidersResults, so this shares its cache entry.
  const result = useQuery({ ...adminProvidersQuery(search), placeholderData: keepPreviousData });
  const filtered = search.q !== undefined || search.status !== undefined || search.kind !== undefined;

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="font-heading text-headline font-semibold text-navy-900">Providers</h1>
        <p className="text-neutral-600">
          {result.data === undefined
            ? 'Loading providers'
            : `${result.data.total.toLocaleString('en-ZA')} ${filtered ? 'matching' : 'in total'}`}
        </p>
      </header>

      <Filters search={search} />

      <ProvidersResults search={search} filtered={filtered} />
    </div>
  );
}

const SORT_COLUMN: Record<string, AdminProviderSort> = { provider: 'name', createdAt: 'createdAt' };

function ariaSort(columnId: string, current: AdminProviderListQueryType) {
  if (SORT_COLUMN[columnId] !== current.sort) return undefined;
  return current.dir === 'asc' ? 'ascending' : 'descending';
}

/** The table, or the loading, error and empty states in its place. */
function ProvidersResults({ search, filtered }: { search: AdminProviderListQueryType; filtered: boolean }) {
  const result = useQuery({ ...adminProvidersQuery(search), placeholderData: keepPreviousData });
  const table = useTable({ features, columns, data: result.data?.items ?? emptyRows });

  if (result.isError) {
    return (
      <StatusBanner
        tone="danger"
        title="We could not load the providers"
        description="Check your connection and try again."
        action={
          <Button variant="outline" size="sm" onClick={() => result.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }
  if (result.data === undefined) return <ListSkeleton count={8} shape="row" />;
  if (result.data.items.length === 0) {
    return (
      <EmptyState
        icon={MagnifyingGlass}
        title={filtered ? 'No providers match' : 'No providers yet'}
        description={
          filtered
            ? 'Try fewer words, or clear the filters.'
            : 'Providers appear here as soon as they sign up, while their profile is still a draft.'
        }
        action={
          filtered ? (
            <Button asChild variant="outline" size="sm">
              <Link to="/providers" search={{}}>
                Clear search and filters
              </Link>
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <>
      <div
        className="overflow-x-auto rounded-(--radius-card) border border-border bg-surface"
        aria-busy={result.isFetching}
      >
        <table className="w-full border-collapse text-left">
          <thead className="border-b border-border bg-navy-50">
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    aria-sort={ariaSort(header.column.id, search)}
                    className={`px-3 py-2 text-eyebrow font-semibold text-neutral-700 uppercase ${COLUMN_CLASS[header.column.id] ?? ''}`}
                  >
                    {header.isPlaceholder ? null : <table.FlexRender header={header} />}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={result.isPlaceholderData ? 'opacity-60' : undefined}>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.original.id}
                className="border-t border-border first:border-t-0 hover:bg-navy-50/60"
              >
                {row.getAllCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`max-w-64 px-3 py-2 align-top text-navy-900 ${COLUMN_CLASS[cell.column.id] ?? ''}`}
                  >
                    <table.FlexRender cell={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager total={result.data.total} search={search} />
    </>
  );
}

/** Search box and filters. Each change goes back to page 1. */
function Filters({ search: current }: { search: AdminProviderListQueryType }) {
  const navigate = useNavigate({ from: Route.fullPath });
  const searchId = useId();
  const [text, setText] = useState(current.q ?? '');

  // Keep the box in step when the URL changes underneath it, without trimming what they are still typing.
  useEffect(
    () => setText((typed) => (typed.trim() === (current.q ?? '') ? typed : (current.q ?? ''))),
    [current.q],
  );

  // Search as they type, once they pause. Replace, so typing does not fill the history.
  useEffect(() => {
    const q = text.trim() === '' ? undefined : text.trim();
    if (q === current.q) return;
    const timer = setTimeout(() => {
      void navigate({ search: (prev) => ({ ...prev, q, page: 1 }), replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [text, current.q, navigate]);

  const setFilter = (patch: Partial<AdminProviderListQueryType>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch, page: 1 }) });

  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-end">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <label htmlFor={searchId} className="font-medium text-navy-900">
          Search
        </label>
        <div className="relative">
          <MagnifyingGlass
            size={18}
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-neutral-500"
          />
          <Input
            id={searchId}
            type="search"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Business, contact, email or phone"
            className="pl-9"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:flex">
        <FilterSelect
          label="Status"
          value={current.status}
          onChange={(value) =>
            setFilter({ status: value === '' ? undefined : (value as AdminProviderListQueryType['status']) })
          }
          options={PROVIDER_STATUSES.map((value) => ({ value, label: PROVIDER_STATUS_LABELS[value] }))}
          anyLabel="Any status"
        />
        <FilterSelect
          label="Kind"
          value={current.kind}
          onChange={(value) =>
            setFilter({ kind: value === '' ? undefined : (value as AdminProviderListQueryType['kind']) })
          }
          options={PROVIDER_KINDS.map((value) => ({ value, label: PROVIDER_KIND_LABELS[value] }))}
          anyLabel="Any kind"
        />
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  anyLabel,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  anyLabel: string;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="font-medium text-navy-900">
        {label}
      </label>
      <NativeSelect id={id} value={value ?? ''} onChange={(event) => onChange(event.target.value)}>
        <NativeSelectOption value="">{anyLabel}</NativeSelectOption>
        {options.map((option) => (
          <NativeSelectOption key={option.value} value={option.value}>
            {option.label}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </div>
  );
}

/** A column header that sorts by this column; a second click flips the direction. */
function SortHeader({ sort, label }: { sort: AdminProviderSort; label: string }) {
  const search = Route.useSearch();
  const active = search.sort === sort;
  // Newest first is the useful default for dates; names start at A.
  const firstDir = sort === 'name' ? 'asc' : 'desc';
  const dir = active ? (search.dir === 'asc' ? 'desc' : 'asc') : firstDir;
  const Arrow = active ? (search.dir === 'asc' ? CaretUp : CaretDown) : null;

  return (
    <Link
      from={Route.fullPath}
      to="/providers"
      search={(prev) => ({ ...prev, sort, dir, page: 1 })}
      className="inline-flex min-h-(--spacing-touch) items-center gap-1 whitespace-nowrap hover:text-navy-900"
    >
      {label}
      {Arrow === null ? null : <Arrow size={14} weight="bold" aria-hidden="true" />}
    </Link>
  );
}

function CoverageCell({ row }: { row: AdminProviderRow }) {
  if (row.basedIn === null && row.categories.length === 0) {
    return <span className="text-neutral-600">Not filled in yet</span>;
  }
  return (
    <div className="flex flex-col">
      <span className="truncate">
        {row.basedIn === null ? 'Base not set' : `${row.basedIn.name}, ${row.basedIn.city}`}
      </span>
      <span className="truncate text-eyebrow text-neutral-600">
        {[
          row.categories.length === 0 ? 'No categories' : row.categories.join(', '),
          `${row.serviceAreaCount} ${row.serviceAreaCount === 1 ? 'area' : 'areas'}`,
        ].join(' · ')}
      </span>
    </div>
  );
}

function Pager({ total, search }: { total: number; search: AdminProviderListQueryType }) {
  const navigate = useNavigate({ from: Route.fullPath });
  const pageSizeId = useId();
  const pages = Math.max(1, Math.ceil(total / search.pageSize));
  const first = total === 0 ? 0 : (search.page - 1) * search.pageSize + 1;
  const last = Math.min(total, search.page * search.pageSize);

  return (
    <nav aria-label="Pages" className="flex flex-wrap items-center justify-between gap-3">
      <p className="tabular-nums text-neutral-600">
        {first > total
          ? `Page ${search.page} is past the end`
          : `Showing ${first}–${last} of ${total.toLocaleString('en-ZA')}`}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2">
          <label htmlFor={pageSizeId} className="text-neutral-600">
            Per page
          </label>
          <NativeSelect
            id={pageSizeId}
            value={String(search.pageSize)}
            onChange={(event) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  pageSize: Number(event.target.value) as AdminProviderListQueryType['pageSize'],
                  page: 1,
                }),
              })
            }
            className="w-20"
          >
            {ADMIN_PROVIDER_PAGE_SIZES.map((size) => (
              <NativeSelectOption key={size} value={size}>
                {size}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        <Button asChild={search.page > 1} variant="outline" size="sm" disabled={search.page <= 1}>
          {search.page > 1 ? (
            <Link
              from={Route.fullPath}
              to="/providers"
              search={(prev) => ({ ...prev, page: search.page - 1 })}
            >
              <CaretLeft aria-hidden="true" />
              Previous
            </Link>
          ) : (
            <>
              <CaretLeft aria-hidden="true" />
              Previous
            </>
          )}
        </Button>
        <span className="tabular-nums text-neutral-600">
          {Math.min(search.page, pages)} of {pages}
        </span>
        <Button asChild={search.page < pages} variant="outline" size="sm" disabled={search.page >= pages}>
          {search.page < pages ? (
            <Link
              from={Route.fullPath}
              to="/providers"
              search={(prev) => ({ ...prev, page: search.page + 1 })}
            >
              Next
              <CaretRight aria-hidden="true" />
            </Link>
          ) : (
            <>
              Next
              <CaretRight aria-hidden="true" />
            </>
          )}
        </Button>
      </div>
    </nav>
  );
}
